import { useState, FormEvent } from 'react';
import { Download, Sparkles, Send } from 'lucide-react';
import { usePrototype1 } from '../Prototype1Context';

const SECTION_ID = 'college-eligibility';

const Screen02CollegeEligibilityNotStarted = () => {
  const { chatHistoriesBySection, isChatPending, sendChat, goToScreen, sectionStatuses, setSectionStatus } =
    usePrototype1();
  const [input, setInput] = useState('');
  const chatHistory = chatHistoriesBySection[SECTION_ID] ?? [];
  const hasSentFirst = chatHistory.some((m) => m.role === 'user');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isChatPending) return;
    const message = input;
    setInput('');
    if (sectionStatuses[SECTION_ID] === 'not-started') {
      setSectionStatus(SECTION_ID, 'in-progress');
    }
    await sendChat(message, {
      intent: 'draft',
      active_section_id: SECTION_ID,
      ui_state: { panel: 'chat-open', last_user_action: 'first chat send' },
      response_hint: { shape: 'chat-reply' },
    });
    if (!hasSentFirst) goToScreen('03-equity-gaps-drafting');
  };

  return (
    <div className="flex h-full w-full bg-[#fafafa] font-['Inter']">
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center justify-between px-6 h-14 border-b border-[#e5e5e5] shrink-0">
          <div className="flex items-center gap-2 text-sm">
            <button type="button" onClick={() => goToScreen('01-dashboard')} className="text-[#737373] hover:underline">
              Issaquah School District
            </button>
            <span className="text-[#737373]">/</span>
            <span className="text-[#737373]">Class of 2025</span>
            <span className="text-[#737373]">/</span>
            <span className="font-medium text-[#0a0a0a]">College Eligibility</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold text-white rounded-full bg-[#f59e0b]">
              Drafting
            </span>
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium border border-[#e5e5e5] rounded-md bg-white hover:bg-gray-50 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Export Draft
            </button>
          </div>
        </div>

        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex flex-col gap-6 flex-1 overflow-y-auto px-20 py-8 items-center">
            <div className="flex flex-col gap-6 w-[720px]">
              <div className="flex flex-col items-center gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#171717]">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="flex flex-col gap-1 text-center">
                  <h2 className="text-xl font-semibold text-[#0a0a0a]">College Eligibility</h2>
                  <p className="text-sm text-[#737373]">Section 2 of 7 · Not yet started</p>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#171717] shrink-0">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-[#0a0a0a]">AI Draft Assistant</span>
                  <span className="text-xs text-[#737373]">Just now</span>
                </div>
                <div className="rounded-xl bg-[#fafafa] border border-[#e5e5e5] px-5 py-4 shadow-[0_1px_1.75px_rgba(0,0,0,0.05)]">
                  <p className="text-sm text-[#0a0a0a] leading-[1.6]">
                    I'm ready to help you draft the College Eligibility section. This section will analyze eligibility
                    rates for Issaquah's Class of 2025 — including overall numbers, equity gaps across student
                    subgroups, and the structural factors driving those gaps.
                  </p>
                  <p className="text-sm text-[#0a0a0a] leading-[1.6] mt-3">
                    Would you like me to start drafting, or is there specific data or framing you'd like to include
                    before I begin?
                  </p>
                </div>
              </div>

              {isChatPending && <span className="text-xs text-[#737373]">AI is typing…</span>}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-2.5 px-20 py-3 border-t border-[#e5e5e5] items-center"
          >
            <div className="flex items-center gap-2 w-[720px]">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message or instruction..."
                disabled={isChatPending}
                className="flex-1 h-11 px-3.5 rounded-lg bg-[#fafafa] border border-[#e5e5e5] text-[13px] text-[#0a0a0a] outline-none"
              />
              <button
                type="submit"
                disabled={isChatPending || !input.trim()}
                className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#171717] shrink-0 hover:bg-[#2a2a2a] transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
            <p className="text-[12px] text-[#737373] w-[720px] text-center">
              You can reference specific data, request charts, or guide the narrative direction
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Screen02CollegeEligibilityNotStarted;
